import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertCircle, Info, Eye } from 'lucide-react';
import './DashboardSurMesureView.css';

/**
 * L'hôte d'un dashboard sur mesure.
 *
 * Un dashboard sur mesure est un artefact HTML autonome — écrit sur claude.ai avec le connecteur
 * MCP Kokpit, et demain généré ici — que le serveur assemble avec un préambule puis que ce
 * composant pose dans une iframe en bac à sable. L'hôte ne connaît rien du contenu : il est le
 * pont entre l'artefact et l'API de Kokpit.
 *
 * ## Les trois responsabilités, et pas une de plus
 *
 * 1. **Poser le document** une fois, et ne plus y toucher.
 * 2. **Répondre aux demandes de données** de la frame, en les relayant à la porte du serveur.
 * 3. **Transmettre la période** choisie dans la barre latérale de Kokpit, et le thème.
 *
 * ## ⚠️ Ne jamais remonter l'iframe
 *
 * `srcDoc` n'est pas comparé par React : une chaîne différente recharge le document entier —
 * défilement, focus et état interne perdus. Le document est donc rangé dans un état posé **une
 * seule fois**, et tout ce qui change ensuite — la période, le thème — passe par `postMessage`.
 *
 * C'est le défaut le plus probable de ce composant : il suffit qu'un `useMemo` ou un `key`
 * dépende de la période pour que changer de dates réinitialise le dashboard.
 *
 * ## ⚠️ L'origine de la frame est opaque
 *
 * L'iframe est montée `sandbox="allow-scripts"` **sans** `allow-same-origin`, et ce n'est pas une
 * précaution de principe : `vercel.json` réécrit `/api/*` vers Railway, donc l'API et
 * l'application sont de même origine pour le navigateur. Une frame de même origine lirait le
 * `localStorage` de Kokpit, et donc son jeton.
 *
 * Conséquence : `event.origin` vaut la chaîne `"null"` pour tous les messages de la frame. Le
 * seul identifiant fiable est `event.source`, comparé à notre propre `contentWindow` — et c'est
 * lui qui écarte au passage une frame imbriquée que l'artefact aurait créée. Le `nonce`, émis par
 * le serveur à chaque montage, est la seconde serrure.
 */

/** Hauteur de départ : l'artefact est long, et rétrécir est moins brutal que faire bondir. */
const HAUTEUR_INITIALE = 1400;

const CANAL = 'kokpit-dsm';

/**
 * La correspondance entre une réponse du serveur et le code que l'artefact sait lire.
 *
 * Choisie pour retomber sur les branches que les artefacts de claude.ai ont déjà écrites : un
 * dashboard non adapté dégrade proprement, et sait même réessayer sur `rate_limited` et
 * `server_unavailable`.
 */
function codeDuStatut(statut) {
  if (statut === 401) return { code: 'needs_reauth', retryable: false };
  if (statut === 403) return { code: 'blocked_by_policy', retryable: false };
  if (statut === 429) return { code: 'rate_limited', retryable: true, retryAfterMs: 2000 };
  if (statut >= 500) return { code: 'server_unavailable', retryable: true, retryAfterMs: 2000 };
  // 400, 404 et 409 sont des refus de la porte : ils nomment ce qui ne va pas, et réessayer à
  // l'identique ne changerait rien.
  return { code: 'tool_error', retryable: false };
}

const DashboardSurMesureView = ({
  token,
  dashboardId,
  /**
   * La version à afficher, ou `null` pour celle qui est publiée.
   *
   * C'est ce qui permet de prévisualiser un **candidat** — une version enregistrée mais non
   * promue. Elle lit de vraies données, puisqu'elle vit sur une fiche réelle : c'est la seule
   * façon de juger une écriture avant de la publier.
   */
  versionId = null,
  selectedPeriod,
  customStartDate,
  customEndDate,
  onRetour
}) => {
  const [etat, setEtat] = useState({ chargement: true, erreur: null, fiche: null });
  /**
   * Le document et son nonce, posés une seule fois.
   *
   * Dans leur propre état, et non dans `etat` : un `setEtat` qui recopierait l'objet changerait la
   * référence du document et remonterait l'iframe. Voir l'avertissement en tête de fichier.
   */
  const [cadre, setCadre] = useState(null);
  const [hauteur, setHauteur] = useState(HAUTEUR_INITIALE);
  const [bornage, setBornage] = useState(null);
  const [erreurArtefact, setErreurArtefact] = useState(null);

  const cadreRef = useRef(null);
  const nonceRef = useRef(null);

  /**
   * La période courante, dans une référence autant que dans les props.
   *
   * Le gestionnaire de messages est posé une fois ; sans cette référence il refermerait sur la
   * période du premier rendu, et la frame recevrait des dates périmées à chaque poignée de main.
   */
  const periodeRef = useRef(null);
  periodeRef.current = {
    period: selectedPeriod,
    start_date: selectedPeriod === 'CUSTOM' ? customStartDate : undefined,
    end_date: selectedPeriod === 'CUSTOM' ? customEndDate : undefined
  };

  /* ---------- chargement du document ---------- */

  useEffect(() => {
    if (!token || !dashboardId) return;
    let annule = false;

    const params = new URLSearchParams();
    // La période initiale ne sert qu'à faire calculer `periodeResolue` : le document, lui, n'en
    // dépend pas, et c'est ce qui permet de ne pas le recharger quand elle change.
    if (selectedPeriod) params.set('period', selectedPeriod);
    if (selectedPeriod === 'CUSTOM' && customStartDate) params.set('startDate', customStartDate);
    if (selectedPeriod === 'CUSTOM' && customEndDate) params.set('endDate', customEndDate);
    if (versionId) params.set('versionId', versionId);

    setEtat({ chargement: true, erreur: null, fiche: null });
    setCadre(null);
    setHauteur(HAUTEUR_INITIALE);
    setErreurArtefact(null);

    fetch(`/api/dashboards-sur-mesure/${dashboardId}?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (r) => {
        const corps = await r.json().catch(() => ({}));
        if (!r.ok || !corps.success) {
          throw new Error(corps.error || `Le dashboard n'a pas pu être chargé (${r.status}).`);
        }
        return corps;
      })
      .then((corps) => {
        if (annule) return;
        nonceRef.current = corps.nonce;
        setEtat({ chargement: false, erreur: null, fiche: corps.dashboard });
        setBornage(corps.periodeResolue || null);
        setCadre({ document: corps.document, nonce: corps.nonce });
      })
      .catch((e) => {
        if (annule) return;
        setEtat({ chargement: false, erreur: e.message, fiche: null });
      });

    return () => { annule = true; };
    // ⚠️ La période est volontairement absente des dépendances : elle ne doit pas recharger le
    // document. Elle est lue au montage pour le bornage initial, puis transmise par message.
    //
    // `versionId` y est, lui, et c'est la différence : changer de version **est** changer de
    // document. Le remontage de l'iframe est alors ce qu'on veut, pas ce qu'on redoute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dashboardId, versionId]);

  /**
   * ⚠️ **Le `srcdoc` est posé ici, après l'insertion de l'iframe — et non par une propriété JSX.**
   *
   * React pose les attributs d'un élément *avant* de l'insérer dans le document. Une iframe qui
   * reçoit son `srcdoc` alors qu'elle est encore détachée commence à charger `about:srcdoc` dans
   * une frame **sans vue** : Chrome ne lui en attribue jamais, et la frame tourne alors avec
   * `innerWidth` et `innerHeight` à zéro.
   *
   * Le symptôme est déroutant, parce que tout le reste marche : la page s'exécute, la poignée de
   * main aboutit, les six requêtes partent et reviennent — mais rien ne se met en page, donc
   * `scrollHeight` vaut zéro, aucune hauteur n'est annoncée, et l'iframe garde sa hauteur de
   * départ en n'affichant rien. Mesuré dans Chrome : l'hôte voyait 1176 × 1400 pendant que la
   * frame se mesurait à 0 × 0.
   *
   * Poser l'attribut sur un élément déjà dans le document suffit à lui donner sa vue.
   */
  useEffect(() => {
    if (!cadre || !cadreRef.current) return;
    cadreRef.current.setAttribute('srcdoc', cadre.document);
  }, [cadre]);

  /* ---------- le pont ---------- */

  const envoyerAuCadre = useCallback((message) => {
    const fenetre = cadreRef.current?.contentWindow;
    if (!fenetre || !nonceRef.current) return;
    // `'*'` est le seul ciblage possible : l'origine de la frame est opaque, elle n'a pas d'autre
    // adresse. Sans conséquence, l'unique destinataire étant la fenêtre que nous avons créée et
    // que nous adressons par `contentWindow`.
    fenetre.postMessage({ canal: CANAL, version: 1, cadre: nonceRef.current, ...message }, '*');
  }, []);

  /**
   * Transmet la période et le thème à la frame.
   *
   * ⚠️ **La période demandée et la plage servie sont deux choses, et le contexte les sépare.** La
   * demandée est ce que le lecteur a choisi dans la barre latérale : l'hôte la connaît toujours,
   * et c'est elle qui sert de clé de cache dans la frame. La servie est ce que le serveur a
   * réellement lu après bornage au plancher — l'hôte ne l'apprend qu'avec la première réponse,
   * donc *après* avoir transmis le contexte. Les confondre ferait l'un des deux défauts : une
   * clé périmée qui ne recharge rien, ou un pied de page qui annonce la période précédente.
   */
  const envoyerContexte = useCallback(() => {
    const fiche = etat.fiche;
    const demande = periodeRef.current;
    envoyerAuCadre({
      type: 'contexte',
      contexte: {
        periode: {
          period: demande.period,
          demande: { start_date: demande.start_date || null, end_date: demande.end_date || null },
          // La plage réellement servie, pour l'affichage seulement.
          startDate: bornage?.servie?.start_date,
          endDate: bornage?.servie?.end_date,
          start_date: bornage?.servie?.start_date,
          end_date: bornage?.servie?.end_date,
          ajustements: bornage?.ajustements || [],
          message: bornage?.message || null
        },
        theme: 'light',
        dashboard: fiche
          ? {
            id: fiche.id,
            nom: fiche.nom,
            sousTitre: fiche.sousTitre,
            datePlancher: fiche.datePlancher,
            parametres: fiche.parametres || {}
          }
          : null
      }
    });
  }, [envoyerAuCadre, etat.fiche, bornage]);

  /**
   * Relaie une demande de données vers la porte du serveur.
   *
   * La frame n'envoie que ce qu'elle veut lire ; **l'hôte y ajoute la période** prise au sélecteur
   * de Kokpit. La source, le compte et le bornage sont décidés par le serveur, qui ignore tout ce
   * qu'il ne reconnaît pas.
   */
  const relayer = useCallback(async (id, requete) => {
    try {
      const r = await fetch(`/api/dashboards-sur-mesure/${dashboardId}/data`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...requete, ...periodeRef.current })
      });
      const corps = await r.json().catch(() => null);

      if (!r.ok || !corps || corps.success === false) {
        const base = codeDuStatut(r.status);
        envoyerAuCadre({
          type: 'reponse',
          id,
          ok: false,
          error: {
            ...base,
            // Le message vient de Kokpit, en français, et nomme le geste quand il en existe un.
            message: (corps && (corps.error || corps.hint)) || 'La lecture des données a échoué.'
          }
        });
        return;
      }

      // La période servie peut avoir été bornée : c'est la réponse qui fait foi, et le bandeau
      // la suit. Sans cela, changer de période laisserait un bandeau périmé à l'écran.
      if (corps.periode) setBornage(corps.periode);

      envoyerAuCadre({ type: 'reponse', id, ok: true, payload: corps, storedAt: Date.now() });
    } catch (e) {
      envoyerAuCadre({
        type: 'reponse',
        id,
        ok: false,
        error: { code: 'server_unavailable', retryable: true, retryAfterMs: 2000, message: e.message }
      });
    }
  }, [dashboardId, token, envoyerAuCadre]);

  useEffect(() => {
    if (!cadre) return;

    const surMessage = (ev) => {
      /**
       * ⚠️ La seule vérification qui vaille.
       *
       * `ev.origin` vaut la chaîne `"null"` : comparer à une URL rejetterait tout, et comparer à
       * `"null"` accepterait n'importe quelle autre frame en origine opaque de la page. Seul
       * `ev.source` identifie la nôtre.
       */
      if (ev.source !== cadreRef.current?.contentWindow) return;
      const d = ev.data;
      if (!d || d.canal !== CANAL || d.cadre !== nonceRef.current) return;

      if (d.type === 'pret') { envoyerContexte(); return; }
      if (d.type === 'donnees') { relayer(d.id, d.requete || {}); return; }
      if (d.type === 'hauteur') {
        const px = Number(d.px);
        if (Number.isFinite(px) && px > 0) setHauteur(Math.ceil(px));
        return;
      }
      if (d.type === 'erreur-js') {
        // Un artefact cassé derrière une iframe échouerait en silence : personne n'ouvre la
        // console d'une frame. On le dit dans l'hôte, qui est regardé.
        setErreurArtefact(`${d.message}${d.ligne ? ` (ligne ${d.ligne})` : ''}`);
      }
    };

    window.addEventListener('message', surMessage);
    return () => window.removeEventListener('message', surMessage);
  }, [cadre, envoyerContexte, relayer]);

  /**
   * La période a changé, ou l'on vient d'apprendre la plage réellement servie : on le dit à la
   * frame, sans la remonter.
   *
   * ⚠️ **`bornage` est dans les dépendances, et ce n'est pas une boucle.** Une réponse met à jour
   * le bornage, donc réémet le contexte ; mais le préambule compare la période *demandée* et,
   * celle-ci n'ayant pas bougé, se contente de redessiner sans relire. Sans cette dépendance, le
   * pied de page de la frame annoncerait indéfiniment les dates de la période précédente.
   */
  useEffect(() => {
    if (!cadre) return;
    envoyerContexte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, customStartDate, customEndDate, bornage, cadre]);

  /* ---------- rendu ---------- */

  if (etat.chargement) {
    return (
      <div className="kdsm-attente">
        <div className="kdsm-attente-barre" />
        <p>Chargement du dashboard…</p>
      </div>
    );
  }

  if (etat.erreur) {
    return (
      <div className="kdsm-erreur">
        <AlertCircle size={20} />
        <div>
          <strong>Ce dashboard ne peut pas être affiché</strong>
          <p>{etat.erreur}</p>
        </div>
        {onRetour && (
          <button type="button" className="kdsm-retour" onClick={onRetour}>Retour</button>
        )}
      </div>
    );
  }

  return (
    <div className="kdsm-hote">
      {/* Un cadre anonyme pour un lecteur d'écran : le titre nomme ce qu'il contient. */}
      <h1 className="kdsm-titre-masque">{etat.fiche?.nom}</h1>

      {/*
        On regarde un candidat, pas ce que le client voit.

        ⚠️ Le dire est indispensable : l'aperçu d'une version non promue est en tout point
        semblable à la production — mêmes données, même période, même cadre. Sans ce bandeau, on
        juge une écriture en croyant regarder ce qui est publié, ou l'inverse.
      */}
      {etat.fiche?.version && etat.fiche.version.promue === false && (
        <div className="kdsm-bandeau kdsm-bandeau-candidat">
          <Eye size={15} />
          <span>
            Aperçu d'une version non publiée
            {etat.fiche.version.note ? <> — {etat.fiche.version.note}</> : null}
            . Vos clients voient toujours la version en place.
          </span>
        </div>
      )}

      {bornage?.message && (
        <div className="kdsm-bandeau">
          <Info size={15} />
          <span>{bornage.message}</span>
        </div>
      )}

      {erreurArtefact && (
        <div className="kdsm-bandeau kdsm-bandeau-alerte">
          <AlertCircle size={15} />
          <span>Ce dashboard a rencontré une erreur&nbsp;: {erreurArtefact}</span>
        </div>
      )}

      <iframe
        ref={cadreRef}
        className="kdsm-cadre"
        title={etat.fiche?.nom || 'Dashboard sur mesure'}
        /* ⚠️ Pas de `srcDoc` ici : il est posé après insertion, par l'effet ci-dessus. Le mettre
           en propriété JSX laisse Chrome charger le document dans une frame sans vue. */
        /* Sans `allow-same-origin` : l'API et l'application sont de même origine pour le
           navigateur, une frame de même origine lirait le jeton de Kokpit. Sans `allow-modals`
           non plus — un `alert()` dans l'artefact bloquerait l'hôte. */
        sandbox="allow-scripts"
        /* L'artefact charge des polices Google : sans cela, la requête de police partirait avec
           l'URL de la page, et donc l'identifiant du client, en référent. */
        referrerPolicy="no-referrer"
        allow=""
        style={{ height: `${hauteur}px` }}
      />
    </div>
  );
};

export default DashboardSurMesureView;
